/**
 * Webhook create page.
 *
 * Layout:
 *   1. Delivery type   - kind picker (Webhook today; Email / Workflow
 *                        action visible but disabled until those flags GA).
 *                        Not a form input - the choice is a routing
 *                        signal not a value.
 *   2. Configuration   - description, endpoint URL, max attempts,
 *                        subscribed events. Single generated useForm
 *                        (CLAUDE.md "hand-rolled forms are banned").
 *
 * Submits to POST /api/admin/webhooks/. Response carries the plaintext
 * HMAC secret exactly once; handed to the reveal dialog. After
 * acknowledge we navigate to the webhooks list and invalidate the
 * cached query so the new row appears.
 */

import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router'
import { ArrowLeft, ArrowUpRight, Loader2, Mail, Settings } from 'lucide-react'

import { FLAGS, useFeatureFlag } from '@/admin/hooks/use-feature-flag'
import { usePulseWebhooks } from '@/admin/hooks/use-pulse-webhooks'
import api from '@/admin/lib/api'
import { toastApiError } from '@/shared/lib/api-error'
import { Button } from '@/shared/components/ui/button'
import { useForm } from '@/shared/lib/forms'
import type { FormField } from '@/shared/lib/forms'
import { cn } from '@/shared/lib/utils'
import type {
  PulseConsumerKind,
  PulseWebhookSubscription,
} from '@/admin/types/pulse'

import { EventMultiSelect } from './event-multi-select'
import { RevealWebhookSecretDialog } from './reveal-webhook-secret-dialog'
import { buildAuthPayload, buildWebhookAuthFields } from './webhook-auth-fields'

const FORM_ID = 'pulse-webhook-create-form'

// ---------------------------------------------------------------------------
// Kind picker
// ---------------------------------------------------------------------------

interface KindCardProps {
  kind: PulseConsumerKind
  title: string
  description: string
  icon: React.ReactNode
  selected: boolean
  enabled: boolean
  onClick: () => void
}

function KindCard({
  title,
  description,
  icon,
  selected,
  enabled,
  onClick,
}: KindCardProps) {
  return (
    <Button
      variant="ghost"
      onClick={enabled ? onClick : undefined}
      disabled={!enabled}
      className={cn(
        'h-auto flex-col items-start gap-2 rounded-[8px] border-[0.5px] p-4 text-left transition-colors hover:bg-transparent',
        selected
          ? 'border-[var(--ink)] bg-[var(--bg-subtle)]'
          : 'border-[var(--rule)] bg-[var(--bg)]',
        enabled
          ? 'cursor-pointer hover:border-[var(--rule-strong)]'
          : 'cursor-not-allowed opacity-50',
      )}
    >
      <div className="flex items-center gap-2.5">
        <div
          className={cn(
            'flex h-6 w-6 items-center justify-center rounded-md',
            selected
              ? 'bg-[var(--green-bg)] text-[var(--green)]'
              : 'bg-[var(--bg-subtle)] text-[var(--ink-3)]',
          )}
        >
          {icon}
        </div>
        <span className="text-[13px] font-medium text-[var(--ink)]">{title}</span>
      </div>
      <p className="whitespace-normal text-[11.5px] font-light leading-relaxed text-[var(--ink-3)]">
        {description}
      </p>
    </Button>
  )
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export function PulseWebhookCreatePage() {
  const navigate = useNavigate()
  const { invalidate: invalidateWebhooks } = usePulseWebhooks()

  // Only webhooks today - the kind picker is shown for product context
  // but Email / Workflow action are visually disabled. When those flags
  // turn on for the tenant, this state becomes user-controllable.
  const webhooksEnabled = useFeatureFlag(FLAGS.PULSE_WEBHOOKS)
  const emailEnabled = useFeatureFlag(FLAGS.PULSE_EMAIL_TRIGGERS)
  const workflowEnabled = useFeatureFlag(FLAGS.PULSE_WORKFLOW_ACTIONS)
  const [kind] = useState<PulseConsumerKind>('WEBHOOK')

  const [submitting, setSubmitting] = useState(false)
  const [created, setCreated] = useState<PulseWebhookSubscription | null>(null)

  const fields = useMemo<FormField[]>(
    () => [
      {
        type: 'text',
        name: 'description',
        label: 'Description',
        placeholder: 'Claim lifecycle to Acme CRM',
        helperText: 'For your own reference - shown in the webhooks list.',
        value: '',
        autoFocus: true,
      },
      {
        type: 'text',
        name: 'target_url',
        label: 'Endpoint URL',
        placeholder: 'https://hooks.your-company.com/iwarranty',
        helperText:
          'Must be HTTPS. Private/internal IPs are blocked; redirects are not followed.',
        value: '',
        required: 'Endpoint URL is required.',
        validate: (raw) => {
          const v = typeof raw === 'string' ? raw.trim() : ''
          if (!v) return 'Endpoint URL is required.'
          if (!/^https:\/\//i.test(v)) return 'Must be HTTPS.'
          return undefined
        },
      },
      {
        type: 'number',
        name: 'max_attempts',
        label: 'Max attempts',
        helperText:
          'Between 1 and 7. Backoff: 0s · 30s · 2m · 10m · 1h · 6h · 24h.',
        value: 5,
        validate: (raw) => {
          const n = Number(raw)
          if (!Number.isInteger(n) || n < 1 || n > 7)
            return 'Must be between 1 and 7.'
          return undefined
        },
      },
      ...buildWebhookAuthFields(),
      {
        type: 'custom',
        name: 'enabled_events',
        label: 'Subscribed events',
        value: [] as string[],
        validate: (raw) => {
          const list = Array.isArray(raw) ? raw : []
          if (list.length === 0) return 'Select at least one event.'
          return undefined
        },
        render: (formCtx) => {
          const value = formCtx.values['enabled_events'] as
            | string[]
            | undefined
          const selected = new Set(value ?? [])
          return (
            <EventMultiSelect
              selected={selected}
              onToggle={(id) => {
                const next = new Set(selected)
                if (next.has(id)) next.delete(id)
                else next.add(id)
                formCtx.setFieldValue('enabled_events', Array.from(next))
              }}
            />
          )
        },
      },
    ],
    [],
  )

  const { Form: WebhookForm } = useForm(
    {
      name: FORM_ID,
      // 'footer' suppresses the inline submit row - we render our own
      // Cancel/Create buttons below and trigger submit via the HTML
      // `form` attribute on the Create button.
      submit: 'footer',
      fields,
    },
    async (rawValues, formCtx) => {
      const v = rawValues as {
        description: string
        target_url: string
        max_attempts: number | string
        enabled_events: string[]
      }
      const { auth, error: authError } = buildAuthPayload(rawValues)
      if (authError) {
        formCtx.setFieldError(authError.field, authError.message)
        return
      }
      setSubmitting(true)
      try {
        const response = await api.post('/admin/webhooks/', {
          description: (v.description ?? '').trim(),
          target_url: v.target_url.trim(),
          enabled_events: v.enabled_events,
          max_attempts: Number(v.max_attempts),
          ...(auth && auth.type !== 'none' ? { auth } : {}),
        })
        setCreated(response.data as PulseWebhookSubscription)
        invalidateWebhooks()
      } catch (err) {
        toastApiError(err, 'Failed to create webhook')
      } finally {
        setSubmitting(false)
      }
    },
    [],
  )

  return (
    <div className="mt-8 max-w-[820px]">
      {/* Header */}
      <Button
        variant="ghost"
        size="xs"
        onClick={() => navigate('/settings/webhooks')}
        className="mb-3 h-auto gap-1 px-0 py-0 text-[11px] font-normal text-[var(--ink-3)] hover:bg-transparent hover:text-[var(--ink-2)]"
      >
        <ArrowLeft className="h-3 w-3" />
        Back to Webhooks
      </Button>
      <h2 className="text-[15px] font-medium text-[var(--ink)]">
        New subscription
      </h2>
      <p className="mt-0.5 max-w-[600px] text-[12px] text-[var(--ink-3)]">
        Pick how the event should be delivered, then choose which events trigger
        it.
      </p>

      {/* ── Section 1 - Delivery type ───────────────────────────── */}
      <SectionHeader index={1} label="Delivery type" />
      <div className="grid grid-cols-1 gap-2.5 md:grid-cols-3">
        <KindCard
          kind="WEBHOOK"
          title="Webhook"
          description="For pushing platform events into your own backend."
          icon={<ArrowUpRight className="h-3.5 w-3.5" />}
          selected={kind === 'WEBHOOK'}
          enabled={webhooksEnabled}
          onClick={() => void 0}
        />
        <KindCard
          kind="EMAIL"
          title="Email"
          description="Render a template and send it. Used for customer confirmations and operational notices."
          icon={<Mail className="h-3.5 w-3.5" />}
          selected={false}
          enabled={emailEnabled}
          onClick={() => void 0}
        />
        <KindCard
          kind="WORKFLOW_ACTION"
          title="Workflow action"
          description="Run a workflow transition automatically when an event fires. Hands-off automation."
          icon={<Settings className="h-3.5 w-3.5" />}
          selected={false}
          enabled={workflowEnabled}
          onClick={() => void 0}
        />
      </div>

      {/* ── Section 2 - Configuration + events ───────────────────── */}
      <SectionHeader index={2} label="Configuration & events" />
      <Panel>
        <WebhookForm className="flex flex-col gap-4" />
      </Panel>

      {/* Footer */}
      <div className="mt-6 flex items-center justify-end gap-2 border-t-[0.5px] border-t-[var(--rule)] pt-4">
        <Button variant="outline" onClick={() => navigate('/settings/webhooks')}>
          Cancel
        </Button>
        <Button type="submit" form={FORM_ID} disabled={submitting}>
          {submitting ? (
            <>
              <Loader2 className="mr-1.5 h-3 w-3 animate-spin" />
              Creating...
            </>
          ) : (
            'Create subscription'
          )}
        </Button>
      </div>

      {/* One-shot secret reveal - fires on successful create */}
      <RevealWebhookSecretDialog
        open={created !== null}
        secret={created?.secret ?? null}
        description={created?.description}
        onClose={() => {
          setCreated(null)
          navigate('/settings/webhooks')
        }}
      />
    </div>
  )
}

// ---------------------------------------------------------------------------
// Section chrome
// ---------------------------------------------------------------------------

function SectionHeader({ index, label }: { index: number; label: string }) {
  return (
    <h3 className="mt-7 mb-3 text-[10px] font-normal uppercase tracking-[0.16em] text-[var(--ink-3)]">
      {index}. {label}
    </h3>
  )
}

function Panel({ children }: { children: React.ReactNode }) {
  return (
    <div className="rounded-[8px] border-[0.5px] border-[var(--rule)] bg-[var(--bg)] p-5">
      {children}
    </div>
  )
}
